import moment from 'moment-timezone';
import { Record, List } from 'immutable';
import _ from 'lodash';

import config from '../config';
import { sum } from '../helpers/Immutable';
import Station from './Station';
import trainTime from '../lib/TrainTime';
import OrderTrip from './order/Trip';
import AmendedTicket from './amend/Ticket';
import DeliveryOption from './DeliveryOption';
import { AccountPages, Brands, OrderStates, JourneyDirections } from '../constants/AppConstants';
import OrderFulfilment from './order/Fulfilment';
import OrderPayments from './order/Payments';
import OrderSundry from './order/Sundry';
import OrderDiscount from './OrderDiscount';
import { validateOrderJourneys, HandoffState } from '../helpers/DelayRepayOrderHandoff';
import { construct, fields, resolve, resolveAll, compareLinkIds } from '../helpers/LinkMapper';
import { SmartcardErrorBlocking, SmartCardErrorBlockingForLoggedIn, SmartcardErrorNonBlocking } from '../constants/AppConstants';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import UserStore from "../stores/UserStore";
import fpUtils from '../utils/fp-utils';
import { buildSplitGroup, mergeReturnSingles, sortFaresBySplitSection, earliestDepartureTime, isSplitLegReturnDirection, getMergedSplitJourneys, groupTripsByPairing } from '../utils/splits';
import ChangeoverTicket from './changeover/Ticket';
import urls from '../urls';

const Order = new Record({
    id: null,
    email: null,
    brand: null,
    charity: null,
    customerId: null,
    customerName: null,
    expiry: null,
    trips: new List(),
    delivery: new OrderFulfilment(),
    collectionReference: null,
    amendedTickets: new List(),
    changeoverTickets: new List(),
    state: null,
    discount: new OrderDiscount(),
    totalPrice: null,
    leftToPay: null,
    amendPriceDiff: null,
    nlc: null,
    creationDate: null,
    purchaseDate: null,
    originalOrderPurchaseDate: null,
    lastUpdated: null,
    availableDeliveryOptions: new List(),
    payments: null,
    changeoverPayments: null,
    telephoneNumber: null,
    isPartialRefund: false,
    isFullRefund: false,
    isChangeoverRefund: false,
    alreadyPaidForRefund: false,
    refundCanBeAttemptedOnline: false,
    refundCanBeAttemptedOffline: false,
    refundsLocked: false,
    sundries: new List(),
    isInterop: false,
    channel: null,
    totalRewardPoints: null,
    rewardPointUid: null,
    rewardName: null,
    promotionalDiscount: null
});

Object.assign(Order.prototype, {
    isGuestOrder() {
        return !this.customerId;
    },

    isSmartcard() {
        return !!this.trips.size && this.trips.some(trip => trip.isSmartcard());
    },

    isMultiSmartcard() {
        return this.isSmartcard() && (this.trips.size > 1 || this.getTickets().size > 1);
    },

    isSmartcardWithProductDelivery() {
        if (this.isSmartcard()) {
            const smartTrip = this.trips.find(trip => trip.isSmartcard());
            return smartTrip.getTickets().some(ticket => !ticket.getSmartcardNumber());
        }
        return false;
    },

    isETicketDeliveryAndCanAmendETickets () {
        return this.delivery.isETicket() && FeatureFlagStore.get('enableETicketAmends');
    },

    isETicketByPostDelivery () {
        return FeatureFlagStore.get('enableETicketByPost') && this.delivery.isETicketByPost();
    },

    canAmend() {
        const isAmendable = (
            (this.delivery.isCollection() || this.isETicketDeliveryAndCanAmendETickets() || this.isETicketByPostDelivery()) &&
            !this.isAmending() &&
            !this.isCollected() &&
            this.trips.some(x => x.canAmend())
        );

        if (this.hasSplitItems()) {
            return !!FeatureFlagStore.get('enableSplitTicketAmends') && isAmendable;
        }
        return isAmendable;
    },

    hasDaySaveTrip() {
        return this.trips.some(trip => trip.getDaySaveFare());
    },

    hasRoversRangersTrip() {
        return this.trips.some(trip => trip.getRoversRangersFare());
    },

    hasTransPennineStaffRoverTrip() {
        return this.trips.some(trip => trip.isTransPennineStaffRover());
    },

    hasSeasonTrip() {
        return this.trips.some(trip => trip.getSeasonFare());
    },

    hasPartialFulfilment () {
        return this.delivery;
    },

    getSeasonTrips() {
        return this.trips.filter(trip => trip.getSeasonFare());
    },

    numberOfSeasonTrips () {
        return this.trips.filter((trip) => trip.getSeasonFare()).size;
    },

    hasFulfilment({disableAddressCheck = false, linkedSmartcards = false} = {}) {
        return (
            this.delivery &&
            this.delivery.isValid(this.trips, {disableAddressCheck, linkedSmartcards}, this.getSmartcardOption())
        );
    },

    getTodOption() {
        return this.availableDeliveryOptions.find(deliveryOption => deliveryOption.isCollection());
    },

    getPendingAmendedTickets() {
        return this.amendedTickets.filter(x => !x.amended);
    },

    // Groups amended/changeover tickets by passenger, so the same passenger's legs of a split
    // stay together while different passengers on the same split leg are kept apart. Shared by
    // getGroupedPendingAmendedTickets (Confirm step) and getGroupedOldFaresForFares (previous
    // journey on the Summary step) — both need the identical passenger-grouping rules, just
    // over different ticket lists.
    _groupTicketsByPassenger(tickets) {
        const ticketArray = Array.from(tickets);
        // amendedTrip/changeoverTrip is a historical trip snapshot from before this amend, which
        // may belong to a split group that no longer exists in this.trips (the order's current
        // trips, from a later amend). Resolve cross-references against the batch's own
        // historical trips instead, since they were siblings at the time.
        const historicalTrips = ticketArray
            .map(ticket => ticket.amendedTrip || ticket.changeoverTrip)
            .filter(Boolean);
        const groups = new Map();
        const keyOrder = [];

        ticketArray.forEach((ticket) => {
            const trip = ticket.amendedTrip || ticket.changeoverTrip;
            const oldTicketId = ticket.amendedTicket != null ? ticket.amendedTicket : ticket.changeoverTicket;
            const splitSalt = trip?.isSplitTrip?.() ? trip.splitSalt : null;
            const isReturn = trip
                ? trip.isPartOfSplitAsRegularReturnSingle() || isSplitLegReturnDirection(trip, historicalTrips)
                : false;
            // splitSalt/isReturn alone don't distinguish passengers travelling on the same
            // split leg — key on passenger position within the fare too, so each passenger
            // gets their own group, same as the regular (non-split) branch does via oldTicketId.
            // -1 (not null) so the `passengerIndex >= 0` check below can't accidentally pass:
            // `null >= 0` is true in JS, which would silently merge different passengers.
            const passengerIndex = splitSalt != null && ticket.getPassengerIndex
                ? ticket.getPassengerIndex()
                : -1;
            const passengerKey = passengerIndex >= 0 ? passengerIndex : `unresolved_${oldTicketId}`;
            const key = splitSalt != null
                ? `split_${splitSalt}_${isReturn}_${passengerKey}`
                : `regular_${oldTicketId}`;

            if (!groups.has(key)) {
                groups.set(key, []);
                keyOrder.push(key);
            }
            groups.get(key).push(ticket);
        });

        return keyOrder
            .map(key => groups.get(key))
            .sort((groupA, groupB) =>
                earliestDepartureTime(groupA.map(ticket => ticket.amendedTrip || ticket.changeoverTrip)) -
                earliestDepartureTime(groupB.map(ticket => ticket.amendedTrip || ticket.changeoverTrip))
            );
    },

    getGroupedPendingAmendedTickets() {
        return this._groupTicketsByPassenger(this.getPendingAmendedTickets());
    },

    getSmartcardOption() {
        return this.availableDeliveryOptions.find(deliveryOption => deliveryOption.isSmartcard());
    },

    getSTicketOption() {
        return this.availableDeliveryOptions.find(deliveryOption => deliveryOption.isSTicket());
    },

    isTooSoonToCollectOnSmartcard () {
        const enableForcedAppCollectionForTwoHourCollectionWindow = FeatureFlagStore.get('enableForcedAppCollectionForTwoHourCollectionWindow');

        if (enableForcedAppCollectionForTwoHourCollectionWindow) {
            return false;
        }

        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption) {
            return false;
        }

        const { errors } = smartcardOption;

        return errors && errors.some((err => SmartcardErrorNonBlocking.APP_COLLECTION_ONLY in err));
    },

    insufficientActiveSmartcardsToCompleteLinkingOrder () {
        const enableSmartcardAssociations = FeatureFlagStore.get('enableSmartcardAssociations');

        const smartcardOption = this.getSmartcardOption();

        if (!enableSmartcardAssociations || !smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        if (errors && errors.some((err => SmartCardErrorBlockingForLoggedIn.NOT_ENOUGH_ACTIVE_SMARTCARDS_ERROR in err))) {
            return true;
        }

        return false;
    },

    isSmartcardDeliveryOnly() {
        return this.availableDeliveryOptions.size === 1 && !!this.getSmartcardOption();
    },

    isSTicketDeliveryOnly() {
        return this.availableDeliveryOptions.size === 1 && !!this.getSTicketOption();
    },

    tooManyTicketsForNonLinkedSmartOrder(ignoreDeliveryMethods) {
        if (!ignoreDeliveryMethods && !this.delivery.isSmartcard()) {
            return false;
        }

        const enableSmartcardAssociations = FeatureFlagStore.get('enableSmartcardAssociations');

        if (!enableSmartcardAssociations) {
            const tooManyTicketsForSmartcardOnlyDelivery =
                this.getTickets().size > 1 &&
                (ignoreDeliveryMethods ? true : this.isSmartcardDeliveryOnly());

            return tooManyTicketsForSmartcardOnlyDelivery;
        }

        return false;
    },

    smartcardCantBeDeliveredInTime(associatedSmartcards, smartcard) {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.CANT_DELIVER_IN_TIME
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        const isUserLoggedIn = UserStore.getCurrent().isLoggedIn();

        if (isUserLoggedIn && smartcardOption.isDeliverableOnTime === false && (!smartcard && (associatedSmartcards && associatedSmartcards.size < 1))) {
            return true;
        }

        return false;
    },

    smartcardInvalid() {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.PENDING_SMARTCARD,
            SmartcardErrorBlocking.EXPIRED_SMARTCARD,
            SmartcardErrorBlocking.INVALID_SMARTCARD
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        return false;
    },

    smartcardTypeFareAgeMismatch() {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.NOT_CORRECT_AGE,
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        return false;
    },

    smartcardHasPendingProducts() {
        /*
            We only block the purchase of further products on a Smartcard
            that has pending (uncollected) products for GTR brands
        */
        if (!FeatureFlagStore.get('enablePendingActionlistSmartcardDeliveryBlocking')) {
            return false;
        }

        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.PENDING_ACTIONLIST,
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        return false;
    },

    canBeFulfilledToSmartcard(options = {}) {
        const {
            includesChildPreencode,
            ignoreNewSmartcardDeliveryTime,
            ignoreDeliveryMethods
        } = options;

        return !this.isTooSoonToCollectOnSmartcard()
            && !includesChildPreencode
            && !this.tooManyTicketsForNonLinkedSmartOrder(ignoreDeliveryMethods)
            && !this.smartcardTypeFareAgeMismatch()
            && !this.smartcardInvalid()
            && !this.smartcardHasPendingProducts()
            && (!ignoreNewSmartcardDeliveryTime ? !this.smartcardCantBeDeliveredInTime() : true);
    },

    hasSmartcardDeliveryErrors() {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption) {
            return false;
        }

        const { errors } = smartcardOption;
        return !!errors;
    },

    hasSTicketDeliveryErrors() {
        const sTicketOption = this.getSTicketOption();

        if (!sTicketOption) {
            return false;
        }

        const { errors } = sTicketOption;
        return !!errors;
    },

    canBeFulfilled() {
        if (!this.availableDeliveryOptions.size) {
            return false;
        }

        if (this.isSmartcard()) {
            return !!this.getSmartcardOption();
        }

        if (this.isSmartcardDeliveryOnly() && (!this.canBeFulfilledToSmartcard() || this.hasSmartcardDeliveryErrors())) {
            return false;
        }

        if (this.isSTicketDeliveryOnly() && this.hasSTicketDeliveryErrors()) {
            return false;
        }

        /*
            if Smartcard and sTicket are the only delivery options and both have errors,
            then the order cannot be fulfilled
        */
        if (
            !!this.getSmartcardOption() &&
            !!this.getSTicketOption() &&
            this.availableDeliveryOptions.size === 2 &&
            this.hasSmartcardDeliveryErrors() &&
            this.hasSTicketDeliveryErrors()
        ) {
            return false;
        }

        return true;
    },

    isRefundable() {
        return this.canRefundOnline() || this.canRefundOffline();
    },

    canRefund() {
        if (this.hasSplitItems()) {
            return config.get('enableSplitTicketRefunds');
        }
        return true;
    },

    canRefundOnline() {
        // TODO: [CORE-16632]: This is a temporary solution to block refund for changeover tickets
        return this.refundCanBeAttemptedOnline && this.changeoverTickets.size === 0 && this.canRefund();
    },

    canRefundOffline() {
        return this.refundCanBeAttemptedOffline;
    },

    isRefundsLocked() {
        return this.refundsLocked;
    },

    hasCollectionWindowEnded() {
        const now = trainTime();
        const latestCollectionDateBasedOnTrips = this.getLatestCollectionDate();
        const latestCollectionDateBasedOnWindow = this.getCollectionStartDate()
            .clone()
            .add(this.delivery.collectionWindowDays, 'days');

        const latestCollectionDate = latestCollectionDateBasedOnTrips.isBefore(
            latestCollectionDateBasedOnWindow,
        )
            ? latestCollectionDateBasedOnTrips
            : latestCollectionDateBasedOnWindow;

        return latestCollectionDate.isBefore(now);
    },

    getCollectionStartDate() {
        return this.delivery && this.delivery.collectionStartDate
            ? this.delivery.collectionStartDate
            : this.getEarliestCollectionDate();
    },

    getSmartcardCollectionLocation() {
        return (
            this.getIn(['delivery', 'collectionLocation']) ||
            this.getSmartcardOption().suggestedCollectionPoint
        );
    },

    getEarliestCollectionDate() {
        const now = trainTime();
        const depart = this.trips.first().getEarliestTime();
        const earliestDepart = depart
            .clone()
            .subtract(this.delivery.collectionWindowDays - 1, 'days');
        const earliestDepartDate = now.isBefore(earliestDepart) ? earliestDepart : now;
        return earliestDepartDate.startOf('day');
    },

    getLatestCollectionDate() {
        const now = trainTime();
        const latestCollectionDate = this.trips.first().getEarliestTime();
        return now.isBefore(latestCollectionDate) ? latestCollectionDate : now;
    },

    getDiscountCode() {
        return this.getIn(['discount', 'code']);
    },

    getTotalDiscount() {
        return this.getIn(['discount', 'fixedAmount']) > 0
            ? this.discount.get('fixedAmount')
            : null;
    },

    getTotalPrice() {
        return Math.max(this.totalPrice, 0);
    },

    getOriginalTotalPrice() {
        return this.getTotalPriceWithDelivery();
    },

    getTotalPriceWithDelivery() {
        return (
            this.getTripsPrice() +
            this.getDeliveryPrice() -
            this.getTotalDiscount() -
            this.getDiscountCodeValue() +
            this.getBookingFeePrice()
        );
    },

    getTripsPrice() {
        return this.get('trips').reduce((totalPrice, trip) => totalPrice + trip.getTotalPrice(), 0);
    },

    getTripsPriceWithoutDiscount() {
        return this.get('trips').reduce(
            (totalPrice, trip) => totalPrice + trip.getTotalPriceWithoutDiscount(),
            0,
        );
    },

    getRefundAmount() {
        if (this.hasAlternativePayment()) {
            return this.getAlternativePaymentRefundAmount();
        } else {
            return this.getTotalRefundAmount();
        }
    },

    getTrip(id) {
        return this.trips.find(trip => trip.id === id || trip.getCleanId() === id);
    },

    updateTrip(trip, nextTrip) {
        const next = this.trips.set(
            this.trips.findIndex(x => x.id === trip.id),
            nextTrip,
        );
        return this.set('trips', next);
    },

    getTripIds() {
        return this.trips.map(trip => trip.getCleanId());
    },

    setDeliveryOption(option) {
        return this.set('delivery', this.delivery.setOption(option));
    },

    getDeliveryPrice() {
        return this.getIn(['delivery', 'price']);
    },

    getDeliveryMethodName() {
        return this.getIn(['delivery', 'displayName']);
    },

    hasDeliveryAddress() {
        const {delivery} = this;
        if (!delivery || !delivery.isPostalDelivery()) {
            return false;
        }

        const address = delivery.getAddress();
        return address && address.isComplete();
    },

    isPostalDelivery() {
        return this.delivery && this.delivery.isPostalDelivery();
    },

    isSmartcardCollection() {
        return this.delivery && this.delivery.isSmartcard();
    },

    getDeliveryAddress() {
        return this.delivery.getAddress();
    },

    getFirstOrigin() {
        const sorted = this.trips.sort((a, b) => {
            return a.getEarliestTime().isBefore(b.getEarliestTime()) ? -1 : 1;
        });

        const earliest = sorted.first();

        if (!earliest) {
            return new Station();
        } else {
            return earliest.getFirstOrigin();
        }
    },

    isPaid() {
        return _.includes(
            [
                OrderStates.COMPLETE,
                OrderStates.PENDING,
                OrderStates.UNDER_REVIEW,
                OrderStates.FULFILLED,
                OrderStates.FRAUD,
                OrderStates.REFUNDED,
            ],
            this.state,
        );
    },

    isUnderReview() {
        return _.includes([OrderStates.UNDER_REVIEW, OrderStates.FRAUD], this.state);
    },

    isAmending() {
        return OrderStates.AMENDING === this.state;
    },

    isComplete() {
        return OrderStates.COMPLETE === this.state;
    },

    /**
     * Determines whether to show the download receipt button for a given order trip.
     * The button is only visible for the last trip in the order.
     * On the Payment Confirmation and Order Details pages, trips are shown in reverse direction,
     * so the first trip is used for those pages; otherwise, the last trip is used.
     *
     * @param {OrderTrip} orderTrip - The trip to check.
     * @returns {boolean} True if the button should be shown, false otherwise.
     */
    showDownloadReceiptButton(orderTrip) {
        const hasOrderCorrectState = _.includes([OrderStates.COMPLETE, OrderStates.FULFILLED, OrderStates.REFUNDED], this.state);

        // On Payment Confirmation and Order Details pages, trips are shown in reverse direction
        const isOrderHistoryPage = urls.isEndingWithPath(urls.reverse(AccountPages.ACCOUNT_ORDERS_REFUNDS));
        const lastTrip = isOrderHistoryPage ? this.trips.last() : this.trips.first();
        const isLastOrderTrip = !!lastTrip && lastTrip.id === orderTrip.id;

        return hasOrderCorrectState && isLastOrderTrip;
    },

    isCollectAtStation() {
        return this.delivery.isCollection();
    },

    isPrintAtHome() {
        return this.delivery.isPrintAtHome();
    },

    isETicket() {
        return this.delivery.isETicket();
    },

    isSTicket() {
        return this.delivery.isSTicket();
    },

    isCollectable() {
        return this.isCollectAtStation() && this.trips.some(trip => trip.isUnprinted());
    },

    isCollected() {
        return this.isCollectAtStation() && this.trips.every(trip => trip.isPrinted());
    },

    isEmpty() {
        return this.trips.size === 0;
    },

    isCurrentBrand() {
        return (
            _.isEqual(this.brand, config.get('brand')) ||
            config.get('enableAllowRefundsOnOtherBrands')
        );
    },

    brandIsGTR() {
        return Boolean(Object.keys(Brands).find((key) => Brands[key] === this.brand));
    },

    hasExpired() {
        return this.expiry ? this.expiry.isBefore() : false;
    },

    hasRecentlyExpired() {
        return this.expiry ? this.expiry.isAfter(trainTime().subtract(2, 'hours')) : false;
    },

    hasBikeReservations() {
        return this.trips.some(trip => {
            const fares = trip && trip.get('fares');
            if (!fares) {
                return false;
            }

            return fares.some(fare => {
                if (!fare) {
                    return false;
                }

                const outwardJourney = fare.get('outwardJourney');
                const returnJourney = fare.get('returnJourney');

                const outwardHasBikes = outwardJourney && outwardJourney.countBikeSeats() > 0;
                const returnHasBikes = returnJourney && returnJourney.countBikeSeats() > 0;

                return outwardHasBikes || returnHasBikes;
            });
        });
    },

    setAvailableDeliveryOptions(deliveryOptions) {
        let updatedOrder = this.set('availableDeliveryOptions', deliveryOptions);

        const hasBikeReservations = this.hasBikeReservations();

        // If delivery options are available
        if (deliveryOptions.size) {
            // Set it to the first available without an error (like smartcard unavailable)
            return updatedOrder.setDeliveryOption(
                deliveryOptions.find(option =>
                    option.getFirstAvailableDeliveryOption(this.delivery, deliveryOptions, hasBikeReservations)
                )
            );
        }

        // If there are no options, clear the selected
        return updatedOrder.setDeliveryOption(null);
    },

    getFaresIncludedInFavourites (withDelivery = false) {
        return this.get('trips').reduce((acc, trip) =>
            trip.includesFavouriteFare(withDelivery)
                ? acc.concat(trip.getFaresIncludedInFavourites(withDelivery))
                : acc
            , new List()
        );
    },

    includesFavouriteFare (withDelivery = false) {
        return this.getFaresIncludedInFavourites(withDelivery).size > 0;
    },

    hasCardPayment (type = 'payments') {
        return this.getCardPayments(type).size > 0;
    },

    getCardPaymentAmount () {
        const cardPayment = this.getCardPayment();
        return cardPayment && cardPayment.amount || 0;
    },

    getCardPayment () {
        return this.getCardPayments().first();
    },

    getCardPayments (type = 'payments') {
        return this[type] && this[type].getCards() || new List();
    },

    hasLatestVoucherPayment() {
        return this.payments.hasLatestVoucherPayment();
    },

    hasLatestCardPayment() {
        return this.payments.hasLatestCardPayment();
    },

    getLatestCardPaymentDetails() {
        return this.payments.getLatestCardDetails();
    },

    getLatestVoucherPayment() {
        if (!this.hasVoucherPayment()) {
            return null;
        }
        return this.payments.getLatestVoucherPayment();
    },

    getLatestCardPayment() {
        if (!this.hasCardPayment()) {
            return null;
        }
        return this.payments.getLatestCardPayment();
    },

    hasVoucherPayment(type = 'payments') {
        return this.getIn([type, 'warrants'], new List()).size > 0;
    },

    hasPromoCode() {
        return !!this.getPromoCode();
    },

    getPromoCode() {
        return this.promotionalDiscount;
    },

    checkIfThisPromotionalDiscountHasAlreadyApplied(code) {
        return this.getPromoCode() &&
            this.getPromoCode().promoCode &&
            this.getPromoCode().promoCode.toLowerCase() === code.toLowerCase();
    },

    checkIfThisPromoCodeHasAlreadyApplied(code) {
        const tickets = this.getTickets();
        const ticketWithTheSamePromoCode = tickets.find(ticket => ticket.discount && ticket.promoCode === code);
        return Boolean(ticketWithTheSamePromoCode);
    },

    checkIfFixedAmountPromoCodeHasAlreadyApplied(code) {
        const tickets = this.getTickets();
        const ticketWithTheSamePromoCode = tickets.find(ticket => ticket.discount && ticket.discount.name.toLowerCase() === code.toLowerCase());
        return Boolean(ticketWithTheSamePromoCode);
    },

    getVoucherPaymentAmount(type = 'payments') {
        return sum(this[type].warrants || new List(), 'amount');
    },

    getWarrants(type = 'payments') {
        return this.getIn([type, 'warrants']) || new List();
    },

    getLightPromoWarrants(type = 'payments') {
        return this.getWarrants(type).filter((warrant) => warrant.isLightPromo());
    },

    getEVoucherWarrants(type = 'payments') {
        return this.getWarrants(type).filter((warrant) => !warrant.isLightPromo());
    },

    getEVoucherPaymentAmount(type = 'payments') {
        return sum(this.getEVoucherWarrants(type), 'amount');
    },

    getLightPromoPaymentAmount(type = 'payments') {
        return sum(this.getLightPromoWarrants(type), 'amount');
    },

    getCardPaymentsTotal() {
        return sum(this.getCardPayments() || new List(), 'amount');
    },

    hasPaypalPayment(type = 'payments') {
        return this.getIn([type, 'paypal'], new List()).size > 0;
    },

    hasGooglePayPayment(type = 'payments') {
        return this.getIn([type, 'googlePay'], new List()).size > 0;
    },

    hasApplePayPayment(type = 'payments') {
        return this.getIn([type, 'applePay'], new List()).size > 0;
    },

    hasBraintreeCardPayment(type = 'payments') {
        return this.getIn([type, 'cardCheckout'], new List()).size > 0;
    },

    getPaypalPayment(type = 'payments') {
        return this.getIn([type, 'paypal', 0]);
    },

    getApplePayPayment(type = 'payments') {
        return this.getIn([type, 'applePay', 0]);
    },

    getBraintreeCardPayment(type = 'payments') {
        return this.getIn([type, 'cardCheckout', 0]);
    },

    getPaypalPaymentAmount(type = 'payments') {
        return this.getIn([type, 'paypal', 0, 'amount'], 0);
    },

    getGooglePayPayment(type = 'payments') {
        return this.getIn([type, 'googlePay', 0]);
    },

    getGooglePayPaymentAmount(type = 'payments') {
        return this.getIn([type, 'googlePay', 0, 'amount'], 0);
    },

    getApplePayPaymentAmount(type = 'payments') {
        return this.getIn([type, 'applePay', 0, 'amount'], 0);
    },

    getBraintreeCardPaymentAmount(type = 'payments') {
        return this.getIn([type, 'cardCheckout', 0, 'amount'], 0);
    },

    getPaymentRefundAmount(type) {
        return sum(this.getIn(['payments', type], new List()), 'amountRefunded');
    },

    getVoucherRefundAmount() {
        return this.getPaymentRefundAmount('warrants');
    },

    getCardRefundAmount () {
        return this.getPaymentRefundAmount('cards') + this.getPaymentRefundAmount('hostedCards');
    },

    getPaypalRefundAmount() {
        return this.getPaymentRefundAmount('paypal');
    },

    getAlternativePayment() {
        return this.getIn(['payments', 'alternative', 0]);
    },

    getTotalRefundAmount() {
        return (
            this.getCardRefundAmount() +
            this.getPaypalRefundAmount() +
            this.getVoucherRefundAmount()
        );
    },

    getAlternativePaymentRefundAmount() {
        return this.getIn(['payments', 'alternative', 0, 'amount'], 0);
    },

    getAlternativePaymentType() {
        return this.getIn(['payments', 'alternative', 0, 'paymentType'], 0);
    },

    // this method does not consider eTickets:
    hasTooSoonToCollect() {
        return this.trips.some(trip => trip.isTooSoonToCollect());
    },

    collectionLocationEqualTo(station) {
        const collectionStation = this.getIn(['delivery', 'collectionLocation']);
        return station && collectionStation && station.nlc === collectionStation.nlc;
    },

    hasRefunds() {
        return this.isFullRefund || this.isPartialRefund;
    },

    hasRefundableTicket() {
        return this.trips.some(t => t.hasRefundableTicket());
    },

    hasRefundablePaymentType() {
        // for now only allow card/voucher payments to be refunded
        return this.hasCardPayment() || (this.hasVoucherPayment() && !this.hasPaypalPayment());
    },

    hasAmendablePaymentType() {
        // same as for refunds but separate in case one or the other
        // changes
        return this.hasCardPayment() || (this.hasVoucherPayment());
    },

    hasDownloadableTickets() {
        return (
            this.isPaid() &&
            this.isPrintAtHome() &&
            this.trips.some(t => t.hasDownloadableTickets())
        );
    },

    getProducts() {
        return this.trips.map(trip => trip.getProducts()).flatten(1);
    },

    getTickets() {
        return this.getProducts()
            .map(fare => fare.tickets)
            .flatten(1);
    },

    hasChildTickets() {
        return this.getTickets().some(({ children }) => children > 0);
    },

    getFareByTicketId(id) {
        return this.getFares().find(fare => fare.tickets.find(t => t.id === id));
    },

    getFares() {
        return this.getProducts().map(fare => fare);
    },

    hasFlexiFares() {
        return this.getFares().some(x => x.isFlexi());
    },

    arePassengersReady() {
        return !this.trips
            .filter(trip => trip.needsPassengers())
            .some(trip => !trip.hasPassengers());
    },

    hasAmendedTickets() {
        return this.amendedTickets.size > 0;
    },

    hasChangeoverTickets() {
        return this.changeoverTickets.size > 0;
    },

    getChangeoverTrips() {
        return this.changeoverTickets.map(({ changeoverTrip }) => changeoverTrip);
    },

    isNewTicket(id) {
        return [...this.amendedTickets, ...this.changeoverTickets].some(fare => fare.newTicket === id);
    },

    // The amended/changeover ticket records (not yet resolved to fares) whose newTicket is
    // one of the given (new) fares' tickets, deduped by old ticket id.
    _getOldTicketsForFares(fares) {
        const newTicketIds = new Set(
            fares.flatMap(fare => fare.tickets.map(ticket => ticket.id))
                .filter(id => this.isNewTicket(id))
        );

        const seenOldTicketIds = new Set();
        return [...this.amendedTickets, ...this.changeoverTickets]
            .filter(amended => newTicketIds.has(amended.newTicket))
            .filter(amended => {
                const oldTicketId = amended.amendedTicket != null ? amended.amendedTicket : amended.changeoverTicket;
                if (seenOldTicketIds.has(oldTicketId)) {
                    return false;
                }
                seenOldTicketIds.add(oldTicketId);
                return true;
            });
    },

    getOldFaresForFares(fares) {
        return new List(
            this._getOldTicketsForFares(fares)
                .map(amended => amended.getOldFare())
                .filter(Boolean)
        );
    },

    // Same old fares as getOldFaresForFares, but grouped by passenger — each entry is one
    // passenger's old fares (their split's legs, or a single regular fare), matching the shape
    // groupFaresByPassenger produces for the new/current side. Lets the previous-journey display
    // on the Summary step consolidate a passenger's split legs into one row instead of one row
    // per leg per passenger.
    getGroupedOldFaresForFares(fares) {
        return this._groupTicketsByPassenger(this._getOldTicketsForFares(fares))
            .map(group => group.map(amended => amended.getOldFare()).filter(Boolean))
            .filter(group => group.length);
    },

    getAmendComparisonFares(tickets) {
        const seenAmended = new Set();
        const oldFares = tickets
            .filter(ticket => {
                if (seenAmended.has(ticket.amendedTicket)) { return false; }
                seenAmended.add(ticket.amendedTicket);
                return true;
            })
            .map(ticket => ticket.getOldFare())
            .filter(Boolean);

        // Dedupe by the resolved fare's own identity, not the raw newTicket id: when a split is
        // amended into one consolidated (non-split) fare, each old leg's ticket can reference a
        // different one of that fare's own ticket ids — deduping by ticket id alone would then
        // wrongly treat one consolidated fare as two separate (split) ones.
        const seenNewFareIds = new Set();
        const newFares = sortFaresBySplitSection(
            tickets
                .map(ticket => this.getFareByTicketId(ticket.newTicket))
                .filter(Boolean)
                .filter(fare => {
                    if (seenNewFareIds.has(fare.fareId)) {
                        return false;
                    }
                    seenNewFareIds.add(fare.fareId);
                    return true;
                })
        );

        return {
            oldFares,
            newFares,
            isSplitOriginal: oldFares.length > 1,
            isSplitNew: newFares.length > 1,
        };
    },

    hasReachedDiscountLimit(discount) {
        if (!discount || !discount.maxProducts) {
            return false;
        }

        const dicountAppliedCount = this.trips.count(trip => trip.hasDiscount(discount));
        return dicountAppliedCount >= discount.maxProducts;
    },

    hasAlternativePayment() {
        return this.getIn(['payments', 'alternative', 0]);
    },

    hasRailcard1617() {
        return this.getFares().some(fare => fare.hasRailcard1617());
    },

    hasRailcardJCP() {
        return this.getFares().some(fare => fare.hasRailcardJCP());
    },

    hasDisabledPeopleRailcard() {
        return this.getFares().some((fare) => fare.hasDisabledPeopleRailcard());
    },

    getBookingFeePrice() {
        const bookingFeeSundry = this.sundries.find(sundry => sundry.isBookingFee());
        return bookingFeeSundry ? bookingFeeSundry.price : 0;
    },

    getDiscountCodeValue() {
        const discountCodeSundry = this.sundries.find(sundry => sundry.isDiscountCode());
        return discountCodeSundry ? discountCodeSundry.price : 0;
    },

    canClaimDelayRepay() {
        return validateOrderJourneys(this, config.get('tocGroup')).some(
            validation => validation.status === HandoffState.CLAIMABLE,
        );
    },

    numAdultOrChildTickets(adults) {
        return this.getProducts()
        .map(fare => fare.tickets.reduce((acc, ticket) => acc + Number(adults ? ticket.adults > 0 : ticket.children > 0), 0))
        .reduce((acc, numTickets) => acc + numTickets, 0);
    },

    getGroupedRegularAndSplitItemsList() {
        if (!this.trips || this.trips.size === 0) {
            return [];
        }

        const wrappers = [];
        const processedSplitSalts = [];
        this.trips.forEach(item => {
            const itemSplitId = item.getFareSplitId ? item.getFareSplitId() : item.getSplitId();
            const itemSplitSalt = item.splitSalt;
            if (itemSplitId && itemSplitSalt) {
                if (processedSplitSalts.includes(itemSplitSalt)) {
                    return;
                }
                buildSplitGroup(this.trips, itemSplitSalt).forEach(split => wrappers.push({ split }));
                processedSplitSalts.push(itemSplitSalt);
            } else {
                wrappers.push({ regular: item });
            }
        });

        return mergeReturnSingles(wrappers);
    },

    hasSplitItems() {
        return this.trips && this.trips.some(trip => !!trip.getSplitId());
    },

    // Both directions' current, as-booked journeys, given any trip belonging to the order -
    // used to compare against whichever direction isn't the one being amended, so its own
    // trip(s) haven't been touched (see AmendRequest#getUnchangedJourneys). A split trip's
    // direction can span several OrderTrips, so those are merged into one whole-direction journey
    // per direction rather than reading a single leg's journey off referenceTrip directly - both
    // directions come out of that same merge, so they're returned together rather than making a
    // caller needing both re-run the filter/pairing/merge pipeline once per direction.
    getJourneysForBothDirections(referenceTrip) {
        if (!referenceTrip) {
            return { outward: null, inward: null };
        }
        if (referenceTrip.isSplitTrip()) {
            // A splitSalt is reused across every pairing that came out of the same original
            // split purchase, including unrelated pairings left over from a prior
            // partial-passenger amend (see splits.js#groupTripsByPairing) - so it alone isn't
            // enough to isolate referenceTrip's own pairing before merging journeys.
            const tripIdentity = item => (item instanceof OrderTrip ? item.getTripId() : item.id);
            const referenceId = tripIdentity(referenceTrip);
            const splitSaltTrips = this.trips.filter(trip => {
                const fareSplitId = trip.getFareSplitId ? trip.getFareSplitId() : null;
                return !!fareSplitId && trip.splitSalt === referenceTrip.splitSalt;
            });
            const pairing = groupTripsByPairing(splitSaltTrips).find(
                group => group.some(item => tripIdentity(item) === referenceId)
            ) || [];
            const { outwardJourney, returnJourney } = getMergedSplitJourneys(pairing);
            return { outward: outwardJourney, inward: returnJourney };
        }
        return { outward: referenceTrip.getOutwardJourney(), inward: referenceTrip.getReturnJourney() };
    },

    // The order's current, as-booked journey for a single direction - see
    // getJourneysForBothDirections for the shared merge this reads from.
    getJourneyForDirection(direction, referenceTrip) {
        const { outward, inward } = this.getJourneysForBothDirections(referenceTrip);
        return direction === JourneyDirections.INWARD ? inward : outward;
    }
});

Order.fromJSON = function(json) {
    const originalOrderPurchaseDate = resolve(json.originalOrderPurchaseDate || json.purchaseDate).with(trainTime);

    const trips = resolveAll(json.trips)
        .as(OrderTrip)
        .map((trip) => {
            if (!trip || !trip.fares || !originalOrderPurchaseDate) {
                return trip;
            }
            return trip.set('fares', trip.fares.withOriginalPurchaseDate(originalOrderPurchaseDate));
        })
        .sort((orderTripA, orderTripB) => compareLinkIds(orderTripA.id, orderTripB.id));
    const tickets = trips.map(trip => trip.getTickets()).flatten(true);
    const refunds = tickets.filter(ticket => ticket.isRefunded);
    const isChangeoverRefund = tickets.some(ticket => ticket.isChangeoverRefund);
    const amendedTickets = resolveAll(json.amendedTickets).as(AmendedTicket);
    const changeoverTickets = resolveAll(json.changeoverTickets).as(ChangeoverTicket);

    return construct(Order, json, {
        id: fields.number,
        expiry: resolve.with(moment),
        trips: trips,
        state: fields.string,
        brand: fields.string,
        nlc: fields.string,
        amendedTickets,
        changeoverTickets,
        email: fields.string,
        charity: json.charity,
        customerId: fields.number,
        customerName: fields.string,
        collectionReference: fields.string,
        discount: json.discount ? resolve.as(OrderDiscount) : new OrderDiscount(),
        totalPrice: fields.number,
        leftToPay: fields.number,
        amendPriceDiff: fields.number,
        creationDate: resolve.with(trainTime),
        purchaseDate: resolve.with(trainTime),
        originalOrderPurchaseDate,
        lastUpdated: resolve.with(trainTime),
        delivery: json.delivery ? resolve.as(OrderFulfilment) : new OrderFulfilment(),
        availableDeliveryOptions: resolveAll.as(DeliveryOption),
        payments: resolve(json.payments || {}).as(OrderPayments),
        changeoverPayments: resolve(json.changeoverPayments || {}).as(OrderPayments),
        telephoneNumber: fields.string,
        isFullRefund: refunds.size > 0 && tickets.size === refunds.size,
        isPartialRefund: refunds.size > 0 && refunds.size < tickets.size,
        isChangeoverRefund,
        refundCanBeAttemptedOnline: fields.bool,
        refundCanBeAttemptedOffline: fields.bool,
        refundsLocked: fields.bool,
        alreadyPaidForRefund: resolve(json.alreadyPaidForRefund).with(Boolean),
        sundries: resolveAll(json.sundries).as(OrderSundry),
        channel: fields.string,
        isInterop: fields.bool,
        totalRewardPoints: fields.number,
        rewardPointUid: fields.string,
        rewardName: fields.string,
        promotionalDiscount: resolve.with(Object),
    });
};

export default Order;
